import { toast } from '@prophecy/ui-v3/Toast';
import { FROZEN_EMPTY_OBJECT } from '@prophecy/utils/constants';
import { useLatestRef, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { nanoid } from 'nanoid';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useInvalidateHomeConversationContent, useInvalidateHomeConversations } from './api';
import { getHomeChatBrokerUrl } from './brokerUrl';
import { applyLivePatch, HOME_CHAT_ELEMENT_TYPES, nextPromptStartTime } from './chatTranscript';
import { HOME_CHAT_TEST_IDS } from './constants';
import { HomeChatBrokerError, HomeChatSocket } from './homeChatSocket';
import { formatError } from './widgetHost';

import type { HomeChatElement, HomeChatMessages } from './chatTranscript';
import type { HomeChatNotification, SandboxState } from './homeChatSocket';

const NO_MESSAGES = FROZEN_EMPTY_OBJECT as HomeChatMessages;
const BROKER_DISCONNECTED = 'Not connected to DataGPT';
// Refusals the broker sends before a turn starts; anything else may have reached the agent.
const REFUSED_BEFORE_TURN = new Set(['busy', 'bad_request', 'sandbox_not_ready']);
export const BUSY_ELSEWHERE = 'DataGPT is still answering this chat in another tab — try again when it finishes.';

export type SandboxStatus = { state: SandboxState; message?: string };

/**
 * The live half of the home chat: one broker socket per page mount, plus the streamed elements that
 * sit over each conversation's loaded history until it is reloaded. Each chat has a pod of its own,
 * shared with every other tab that has the chat open, so busy and pod status are per chat.
 */
export function useHomeChatSession(conversationId: string | undefined, history: HomeChatMessages) {
  const [liveByConversation, setLiveByConversation] = useState<Record<string, HomeChatMessages>>({});
  const [workingIds, setWorkingIds] = useState<ReadonlySet<string>>(new Set());
  // Chats with a turn another tab started.
  const [runningElsewhere, setRunningElsewhere] = useState<ReadonlySet<string>>(new Set());
  const [sandbox, setSandbox] = useState<SandboxStatus>();
  const [chatSandboxes, setChatSandboxes] = useState<Record<string, SandboxStatus>>({});
  const [authorization, setAuthorization] = useState<string>();
  const socketRef = useRef<HomeChatSocket>();
  // Widget cards ask for their view in their own mount effects, which run before this hook's.
  const socketWaiters = useRef<{ resolve: (socket: HomeChatSocket) => void; reject: (error: Error) => void }[]>([]);
  const whenSocket = usePersistentCallback(
    (): Promise<HomeChatSocket> =>
      socketRef.current
        ? Promise.resolve(socketRef.current)
        : new Promise((resolve, reject) => socketWaiters.current.push({ resolve, reject }))
  );
  const invalidateConversations = useInvalidateHomeConversations();
  const invalidateContent = useInvalidateHomeConversationContent();
  const openChatRef = useLatestRef({ conversationId, history });
  const liveRef = useLatestRef(liveByConversation);

  const setWorking = usePersistentCallback((id: string, working: boolean) =>
    setWorkingIds((prev) => toggled(prev, id, working))
  );

  const onNotification = usePersistentCallback((notification: HomeChatNotification) => {
    switch (notification.method) {
      case 'copilot/chat/didUpdate': {
        const { conversationId: target, payload } = notification.params;
        const open = openChatRef.current;
        const history = open.conversationId === target ? open.history : NO_MESSAGES;
        setLiveByConversation((prev) => {
          const next = applyLivePatch(prev[target] ?? NO_MESSAGES, history, payload);
          return next ? { ...prev, [target]: next } : prev;
        });
        break;
      }
      case 'copilot/chat/setName':
        invalidateConversations();
        break;
      case 'sandbox/status': {
        const { conversationId: target, ...status } = notification.params;
        if (target) setChatSandboxes((prev) => ({ ...prev, [target]: status }));
        else setSandbox(status);
        break;
      }
      case 'copilot/chat/turn': {
        const { conversationId: target, running } = notification.params;
        setRunningElsewhere((prev) => toggled(prev, target, running));
        if (!running) invalidateConversations();
        break;
      }
      case 'copilot/chat/snapshot': {
        const { conversationId: target, running, elements } = notification.params;
        setRunningElsewhere((prev) => toggled(prev, target, running));
        // The chat's state as it stands now; a pod that is still up re-announces itself right after,
        // so a dead pod's error must not survive to be toasted again on every revisit.
        setChatSandboxes(({ [target]: _gone, ...rest }) => rest);
        const live = liveRef.current[target] ?? NO_MESSAGES;
        // Half-streamed elements the pod no longer has: their turn ended while this tab was away.
        const stale = Object.values(live).filter(
          (element) => element.role !== 'user' && element.metainfo?.endTime == null && !elements[element.id]
        );
        if (stale.length > 0) invalidateContent(target);
        if (stale.length > 0 || Object.keys(elements).length > 0)
          setLiveByConversation((prev) => {
            const next = { ...prev[target], ...elements };
            for (const element of stale) delete next[element.id];
            return { ...prev, [target]: next };
          });
        break;
      }
      case 'copilot/agent/error':
        toast.error({ content: notification.params.error, 'data-test-id': HOME_CHAT_TEST_IDS.errorToast });
        break;
      case 'session/auth':
        setAuthorization(notification.params.authorization);
        break;
    }
  });

  useEffect(() => {
    const socket = new HomeChatSocket({
      getUrl: getHomeChatBrokerUrl,
      onNotification,
      // A dropped socket pushes no `sandbox/status` of its own — without this, a sandbox last
      // reported 'ready' stays frozen at 'ready' while the connection is actually down and retrying.
      onConnectionChange: (connected) => {
        if (!connected) {
          setSandbox({ state: 'connecting', message: 'Reconnecting to DataGPT' });
          // Nothing tells a dropped socket that another tab's turn ended; the reconnect's snapshot will.
          setRunningElsewhere(new Set());
          // Same for a pod this tab can no longer hear from: its last status must not outlive the drop.
          setChatSandboxes({});
        }
        // A new socket is a new tab to the broker, attached to nothing yet.
        else void socket.openChat(openChatRef.current.conversationId).catch(() => undefined);
      }
    });
    socketRef.current = socket;
    socket.connect();
    const waiters = socketWaiters.current;
    waiters.splice(0).forEach(({ resolve }) => resolve(socket));
    return () => {
      socket.close();
      socketRef.current = undefined;
      // Whoever is still waiting has to fail rather than hang: a widget frame sits on this promise
      // with no timeout of its own, and would spin forever on a socket that is never coming.
      waiters.splice(0).forEach(({ reject }) => reject(new Error(BROKER_DISCONNECTED)));
    };
  }, [onNotification, openChatRef]);

  useEffect(() => {
    void socketRef.current?.openChat(conversationId).catch(() => undefined);
  }, [conversationId]);

  const send = usePersistentCallback(async (targetId: string, message: string, context?: Record<string, unknown>) => {
    const socket = socketRef.current;
    if (!socket) return false;
    const messageId = nanoid();
    const open = openChatRef.current;
    const history = open.conversationId === targetId ? open.history : NO_MESSAGES;
    setLiveByConversation((prev) => {
      const prompt: HomeChatElement = {
        id: messageId,
        role: 'user',
        type: HOME_CHAT_ELEMENT_TYPES.Markdown,
        properties: context ? { text: message, context } : { text: message },
        metainfo: { startTime: nextPromptStartTime({ ...history, ...prev[targetId] }) }
      };
      return { ...prev, [targetId]: { ...prev[targetId], [messageId]: prompt } };
    });
    setWorking(targetId, true);
    try {
      await socket.chat({ chatId: targetId, messageId, message, ...(context ? { context } : {}) });
      return true;
    } catch (error) {
      // The page closed this socket on its way out; there is no one left to tell.
      if (socketRef.current !== socket) return true;
      const refused = error instanceof HomeChatBrokerError && REFUSED_BEFORE_TURN.has(error.type ?? '');
      if (refused) {
        setLiveByConversation((prev) => {
          const messages = { ...prev[targetId] };
          delete messages[messageId];
          return { ...prev, [targetId]: messages };
        });
      }
      toast.error({
        content:
          error instanceof HomeChatBrokerError && error.type === 'busy'
            ? BUSY_ELSEWHERE
            : formatError(error, 'Something went wrong'),
        'data-test-id': HOME_CHAT_TEST_IDS.errorToast
      });
      return !refused;
    } finally {
      setWorking(targetId, false);
      invalidateConversations();
    }
  });

  const stop = usePersistentCallback(() => {
    if (!conversationId) return;
    socketRef.current?.stop(conversationId).catch((error) =>
      toast.error({
        content: formatError(error, 'Something went wrong'),
        'data-test-id': HOME_CHAT_TEST_IDS.errorToast
      })
    );
  });

  const readResource = usePersistentCallback(async (uri: string) => (await whenSocket()).readResource(uri));

  const callTool = usePersistentCallback(async (name: string, args: Record<string, unknown>) =>
    (await whenSocket()).callTool(name, args)
  );

  const respondToUserInput = usePersistentCallback(
    async (params: { id: string; answers: Record<string, string>; cancelled: boolean }) =>
      (await whenSocket()).respondToUserInput({ ...params, conversationId })
  );

  const widgetHost = useMemo(
    () => ({ authorization, readResource, callTool }),
    [authorization, readResource, callTool]
  );

  return {
    widgetHost,
    respondToUserInput,
    liveMessages: (conversationId && liveByConversation[conversationId]) || NO_MESSAGES,
    isWorking: Boolean(conversationId && (workingIds.has(conversationId) || runningElsewhere.has(conversationId))),
    isRunningElsewhere: Boolean(conversationId && runningElsewhere.has(conversationId)),
    /** The broker connection: prompts can be sent once it is ready. */
    sandbox,
    /** The open chat's own pod, once it has been asked for one. */
    chatSandbox: conversationId ? chatSandboxes[conversationId] : undefined,
    send,
    stop
  };
}

function toggled(set: ReadonlySet<string>, id: string, on: boolean): ReadonlySet<string> {
  if (set.has(id) === on) return set;
  const next = new Set(set);
  if (on) next.add(id);
  else next.delete(id);
  return next;
}
